// src/pages/B2B/event-reports/EventReportBuilderPage.tsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ReportBuilderForm } from './components/ReportBuilderForm';
import { ReportPreviewPanel } from './components/ReportPreviewPanel';
import { useGenerateReport } from './api/useGenerateReport';
import { useReportEnv } from './api/useReportEnv';
import { ENV_COLORS } from '@/pages/Admin/shared';
import type { BuilderFormState } from './types';

function defaultTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Phoenix';
  } catch {
    return 'America/Phoenix';
  }
}

const EMPTY_FORM: BuilderFormState = {
  event_name: '',
  from_local: '',
  to_local: '',
  timezone: defaultTimezone(),
  location_label: '',
  image_staging_key: null,
  image_preview_url: null,
  report_type: 'report_for_businesses',
  spotlight_mode: 'auto',
  spotlight_business_id: null,
};

export function EventReportBuilderPage(): React.ReactElement {
  const [form, setForm] = useState<BuilderFormState>(EMPTY_FORM);
  const navigate = useNavigate();
  const [env] = useReportEnv();
  const { generate, loading, error } = useGenerateReport(env);

  const handleSubmit = async () => {
    const created = await generate(form);
    if (created) {
      navigate('/b2b/event-reports');
    }
  };

  return (
    <div className="min-h-screen bg-[#0f0f1a]">
      <div className="max-w-5xl mx-auto px-6 pt-8 pb-16">
        <div className="mb-6">
          <Link to="/b2b/event-reports" className="text-sm text-gray-500 hover:text-gray-300 no-underline">
            Event Reports
          </Link>
          <span className="text-gray-700 mx-2">/</span>
          <span className="text-sm text-gray-400">New Report</span>
        </div>

        <div className="flex items-center gap-3 mb-6">
          <h1 className="text-2xl font-bold text-white font-display">New Event Report</h1>
          <span className={`px-2.5 py-1 rounded-lg border text-xs font-semibold tracking-wide ${ENV_COLORS[env].badge}`}>
            {env.toUpperCase()}
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8">
          <ReportBuilderForm
            env={env}
            form={form}
            onChange={setForm}
            onSubmit={handleSubmit}
            onCancel={() => navigate('/b2b/event-reports')}
            submitting={loading}
            error={error}
          />
          <div>
            <p className="text-xs uppercase tracking-wider text-gray-500 mb-3">Preview</p>
            <ReportPreviewPanel form={form} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default EventReportBuilderPage;
